/**
 * Residuo "Sin desglose" por métrica: Total (summary, a grano de cuenta) menos la suma
 * de las filas visibles de la tabla. Con dimensiones a nivel anuncio, campañas como
 * Performance Max no tienen anuncios individuales: no aparecen como filas pero SÍ
 * cuentan en el Total. Compartido entre DataExplorerTable (fila en pantalla) y
 * pdfExport (misma fila en el reporte exportado).
 *
 * Solo aplica a métricas ADITIVAS (sumables fila a fila). Los ratios (CPA, CTR, ROAS,
 * CPC, CPM, CPV) y los promedios / counts no aditivos (Alcance, Frecuencia, impression
 * share) NO son sumables: su "Total" del summary se recomputa desde las bases, así que
 * un residuo Total−suma sería un número espurio. El criterio canónico de aditividad lo
 * expone el backend en el propio fields_data: una métrica es aditiva si NO está en
 * CALCULATED_METRICS (ratios calculados) ni en NOT_ADDITIVE_METRICS (reach/frecuencia/
 * live-api no aditivas). Nota: FIELD_DATA_BY_ID del backend NO trae `aggregation` (esa
 * key vive solo en el settings.js del Data Node), por eso NO se puede usar acá.
 *
 * Guards de significancia: |residuo| >= 1 y >= 0,1% del total (evita filas por ruido de
 * redondeo; en tablas sin dimensiones el residuo es ~0 y la fila no aparece). Guard de
 * seguridad extra: el residuo de una aditiva es >= 0 (las filas son un subconjunto del
 * Total) — un residuo negativo delata una métrica mal clasificada, así que se descarta.
 */

const RESIDUAL_NEGATIVE_TOLERANCE = -1; // permite ruido de redondeo, descarta negativos reales

function cellCurrentValue(cell) {
    if (typeof cell === 'object' && cell !== null && cell.currentValue !== undefined) {
        return cell.currentValue;
    }
    if (typeof cell === 'object' && cell !== null && cell.value !== undefined) {
        return cell.value;
    }
    return cell;
}

function cellComparisonValue(cell) {
    if (typeof cell === 'object' && cell !== null && cell.comparisonValue !== undefined) {
        return cell.comparisonValue;
    }
    return null;
}

function summaryEntryFor(summary, fieldId) {
    let entry = summary[fieldId];
    if (!entry) {
        const matchingKey = Object.keys(summary).find(
            key => key.toLowerCase() === fieldId.toLowerCase()
        );
        if (matchingKey) entry = summary[matchingKey];
    }
    return entry || null;
}

/**
 * Aditiva = métrica sumable fila a fila. Criterio del backend: no es un ratio calculado
 * ni una métrica no-aditiva declarada. Si por alguna razón las listas no llegan, se
 * asume aditiva (fallback permisivo) — el guard de residuo >= 0 igual filtra los ratios.
 */
export function isAdditiveMetric(fieldsData, fieldId) {
    const calculated = (fieldsData && fieldsData.CALCULATED_METRICS) || [];
    const notAdditive = (fieldsData && fieldsData.NOT_ADDITIVE_METRICS) || [];
    return calculated.indexOf(fieldId) === -1 && notAdditive.indexOf(fieldId) === -1;
}

function sumRows(rows, headerKey, getValue) {
    let total = 0;
    rows.forEach(row => {
        const v = getValue(row[headerKey]);
        if (v !== null && v !== undefined && !isNaN(v)) total += Number(v);
    });
    return total;
}

/**
 * @returns {Object|null} Mapa headerKey -> { residual, comparisonResidual, fieldId },
 *          o null si ninguna métrica tiene un residuo significativo.
 */
export function computeUndisaggregatedResiduals(headers, summary, rows, fieldsData) {
    if (!summary || !rows || rows.length === 0) return null;

    const residuals = {};
    let hasSignificant = false;

    headers.forEach(header => {
        if (!header || !header.key || !header.key.startsWith('metric_')) return;
        const fieldId = header.key.replace(/^metric_/, '');
        if (!isAdditiveMetric(fieldsData, fieldId)) return;

        const summaryValue = summaryEntryFor(summary, fieldId);
        if (!summaryValue) return;
        const total = typeof summaryValue === 'object'
            ? (summaryValue.currentValue ?? summaryValue.value)
            : summaryValue;
        if (total === null || total === undefined || isNaN(total)) return;

        const residual = Number(total) - sumRows(rows, header.key, cellCurrentValue);
        // Guard de seguridad: una aditiva nunca tiene residuo negativo real (las filas
        // son un subconjunto del Total). Un negativo delata un ratio mal clasificado.
        if (residual < RESIDUAL_NEGATIVE_TOLERANCE) return;
        const significant = Math.abs(residual) >= 1
            && Math.abs(residual) / Math.max(Math.abs(Number(total)), 1) >= 0.001;
        if (!significant) return;

        let comparisonResidual = null;
        const comparisonTotal = typeof summaryValue === 'object' ? summaryValue.comparisonValue : null;
        if (comparisonTotal !== null && comparisonTotal !== undefined && !isNaN(comparisonTotal)) {
            const cr = Number(comparisonTotal) - sumRows(rows, header.key, cellComparisonValue);
            if (cr >= RESIDUAL_NEGATIVE_TOLERANCE) comparisonResidual = cr;
        }

        residuals[header.key] = { residual, comparisonResidual, fieldId };
        hasSignificant = true;
    });

    return hasSignificant ? residuals : null;
}
